/**
 * Vite Entry Point
 * Common initialization for all pages
 */

import Quill from "quill";
import "quill/dist/quill.snow.css";

import { Logger } from "./core/logger";
import { translator } from "./translation/translator";
import { appForm } from "./app-form";
import type { QuillConstructor } from "./types/quill";
import {
    bindAppDomReady,
    bindProductImageZoomToggleChange,
    bindProductImageZoomViewportResize,
} from "./app/app-listener";

window.Quill = Quill as unknown as QuillConstructor;

translator.init();
console.log("[Translator] Dictionaries loaded:", Object.keys(translator.translations).length, "keys");
console.log("[Translator] Sample keys:", Object.keys(translator.translations).slice(0, 5));

import { WysiwygSetup } from "./wysiwyg/wysiwyg-setup";
import { PostCreateSetup } from "./post/create/post-create-setup";

import "./util/spinner/spinner";
import { initSpinnerSystem } from "./util/spinner/spinner-listener";
import { initFormValidator } from "./util/form-validator";
import { initializeDropdownMenus } from "./util/dropdown/dropdown-init";
import { dropdownHandler } from "./util/dropdown/dropdown-handler";
import { CommentSetup } from "./comment/comment-setup";
import { initLikeSystem } from "./like/like-setup";
import { ModalSetup } from "./modal/modal-setup";
import { setModalHandlerInstance } from "./modal/modal-handler";
import { SearchMenuSetup } from "./search/menu/search-menu-setup";
import { SearchSuggestSetup } from "./search/suggest/search-suggest-setup";
import { SNSPreviewManagerSetup } from "./sns/preview-manager/sns-preview-manager-setup";
import { FacebookMessengerLinkSetup } from "./integrations/facebook-messenger-link/facebook-messenger-link-setup";
import { LoginHistorySetup } from "./components/login-history/login-history-setup";
import { initProductCardSystem } from "./products/card/product-card-setup";
import { initProductRecommendSystem } from "./products/card/recommend/product-recommend-setup";
import { initPostRecommendSystem } from "./post/recommend/post-recommend-setup";
import { initUserRecommendSystem } from "./user/recommend/user-recommend-setup";
import { initBrandRecommendSystem } from "./brands/card/recommend/brand-recommend-setup";
import { ShowMoreSetup } from "./components/controls/show-more/show-more-setup";
import { ImageZoomSetup } from "./components/controls/image-zoom/image-zoom-setup";
import { initMultiLangText } from "./util/multilang-text";
import { initRegistry } from "./init/init-setup";
import { PostShowSetup, initPostShowActions } from "./post/show/post-show-setup";
import { PostProductLinker } from "./post/linker/product-linker-setup";
import { ProductThumbImageErrorHandlerSetup } from "./post/linker/product-thumb-image-error-handler/product-thumb-image-error-handler-setup";
import { Roles } from "./constants/roles";
import { UserAvatarSetup } from "./user/avatar/avatar-setup";
import { UserEditSetup } from "./user/edit/user-edit-setup";
import { UserFollowSetup } from "./user/follow/user-follow-setup";
import { UserProfileTabsSetup } from "./user/profile/user-profile-tabs-setup";
import { UserProfilePasswordSetup } from "./user/profile/user-profile-password-setup";
import { UserSocialHandleCheckSetup } from "./user/edit/user-social-handle-check-setup";
import { initInfoTooltipSystem } from "./util/tooltip/info-tooltip";
import { SNSLinksPanelSetup } from "./sns/links/sns-links-panel-setup";
import { initMasterDataEdit } from "./master-data-edit/master-data-edit-setup";
import { initMergeTool } from "./master-data-edit/merge-tool/merge-tool-setup";
import { initNotifications } from "./notifications/notifications-setup";
import { UserRegisterSetup } from "./auth/register/user-register-setup";
import { initAvatarImageFallback } from "./util/media/avatar/avatar-image-fallback";
import { InstagramEmbedDebug } from "./sns/extractor/instagram-embed-debug";
import { initModalTitlebarDragAuto, startModalTitlebarDragObserver } from "./util/modal-titlebar-drag-auto";
import { RankingsMonthlyTabsSetup } from "./rankings/monthly/rankings-monthly-tabs-setup";
import { HorizontalSliderSetup } from "./slider/horizontal-slider-setup";
import { VanillaSliderSetup } from "./slider/vanilla-slider-setup";
import { HomeRankingProductTabsSetup } from "./home/home-ranking-product-tabs-setup";
import { TabSliderSetup } from "./components/controls/tab-slider/tab-slider-setup";
import { NavbarHamburgerSetup } from "./components/navbar/hamburger/navbar-hamburger-setup";
import { NavbarSettingsSetup } from "./components/navbar/settings/navbar-settings-setup";
import { PostContentImageFallbackSetup } from "./post/content/post-content-image-fallback-setup";
import { BrandsFilterFormSetup } from "./brands/list/brands-filter-form-setup";
import { initBrandsSidebar } from "./brands/list/brands-sidebar-setup";
import { AutoSubmitToggleSetup } from "./util/toggle/auto-submit-toggle-setup";
import { BrandsInfiniteScrollSetup } from "./brands/list/brands-infinite-scroll-setup";
import { InfiniteScrollSetup } from "./components/infinite-scroll/infinite-scroll-setup";
import { ProductsInfiniteScrollSetup } from "./products/list/products-infinite-scroll-setup";
import { ProductSortToggleSetup } from "./products/list/sort-toggle/product-sort-toggle-setup";
import { ExportPostsReviewToggleSetup } from "./admin-download/export-posts-review-toggle-setup";
import { ExportAsyncSetup } from "./admin-download/export-async-setup";
import { ImportFileClearSetup } from "./admin-download/import-file-clear-setup";
import { PostsInfiniteScrollSetup } from "./post/list/posts-infinite-scroll-setup";
import { BoardMediaImageErrorSetup } from "./post/list/board-media-image-error/board-media-image-error-setup";
import { BoardMediaSliderSetup } from "./post/list/board-media-slider-setup";
import { BoardCardReviewDetailsHandler } from "./post/list/board-card-review-details-handler";
import { BoardCardReviewDetailsForm } from "./post/list/board-card-review-details-form";
import { PostsAdsSetup } from "./ad/posts/posts-ads-setup";
import { ProductPlaceholderAdsSetup } from "./ad/products/product-placeholder-ads-setup";
import { AdClickTrackingSetup } from "./ad/ad-click-tracking-setup";
import { CommonUtils } from "./util/common-utils";
import { JobsDashboardSetup } from "./jobs/dashboard/jobs-dashboard-setup";
import { BoardConfigAdminSetup } from "./board-config/admin/board-config-admin-setup";
import { PointRuleAdminSetup } from "./point-rule/admin/point-rule-admin-setup";
import { AdsAdminSetup } from "./ad/admin/ads-admin-setup";
import { UsersAdminSetup } from "./user/admin/users-admin-setup";
import { DashboardSetup } from "./admin/dashboard/dashboard-setup";
import { MaximizeViewerSetup } from "./post/maximize-viewer/maximize-viewer-setup";
import { directMaximizeReadTracker } from "./post/read/direct-maximize-read-tracker";
import { AboutUsTabsSetup } from "./static/about-us-tabs-setup";
import { setupScrollHide } from "./components/scroll-hide/scroll-hide-setup";
import { setupFloatingChat } from "./components/floating-chat/floating-chat-setup";
import { initFilterSidebarToggle, hideMobileFilterToggleIfInert } from "./products/filter/filter-sidebar-toggle-setup";
import { initFilterSidebar } from "./products/filter/filter-sidebar-setup";

let appInitsRegistered = false;
const PRODUCT_IMAGE_ZOOM_TOGGLE_STORAGE_KEY = "ui.productImageZoomEnabled";

function getStoredProductImageZoomEnabled(): boolean {
    try {
        return globalThis.localStorage?.getItem(PRODUCT_IMAGE_ZOOM_TOGGLE_STORAGE_KEY) === "1";
    } catch {
        return false;
    }
}

function setStoredProductImageZoomEnabled(enabled: boolean): void {
    try {
        globalThis.localStorage?.setItem(PRODUCT_IMAGE_ZOOM_TOGGLE_STORAGE_KEY, enabled ? "1" : "0");
    } catch {
        // no-op (private mode or blocked storage)
    }
}

function syncInfiniteScrollViewportMode(): void {
    InfiniteScrollSetup.setPreferredEnabled(CommonUtils.isMobileViewport(768), document);
}

function applyProductImageZoomEnabled(enabled: boolean): void {
    const effectiveEnabled = enabled && !CommonUtils.isMobileViewport(768);

    appForm.getImageZoomRoots(document).forEach((zoomRoot) => {
        zoomRoot.dataset.imageZoomDisabled = effectiveEnabled ? "0" : "1";
        if (!effectiveEnabled) {
            zoomRoot.dataset.imageZoomActive = "0";
        }
    });

    appForm.getListingProductCards(document).forEach((cardEl) => {
        cardEl.dataset.listImageZoomEnabled = effectiveEnabled ? "1" : "0";
    });
}

function initNavbarImageZoomToggle(): void {
    const toggles = appForm.getProductImageZoomToggles(document);
    const syncZoomToggleUi = (): void => {
        const isMobile = CommonUtils.isMobileViewport(768);
        const enabled = getStoredProductImageZoomEnabled();

        applyProductImageZoomEnabled(enabled);

        toggles.forEach((toggleEl) => {
            if (!(toggleEl instanceof HTMLInputElement)) return;
            toggleEl.disabled = isMobile;
            toggleEl.checked = isMobile ? false : enabled;
            toggleEl.setAttribute("aria-disabled", isMobile ? "true" : "false");
        });
    };

    syncZoomToggleUi();

    if (!toggles.length) return;

    toggles.forEach((toggleEl) => {
        if (!(toggleEl instanceof HTMLInputElement)) return;
        if (toggleEl.dataset.zoomPersistBound === "1") return;
        toggleEl.dataset.zoomPersistBound = "1";

        bindProductImageZoomToggleChange(toggleEl, () => {
            if (CommonUtils.isMobileViewport(768)) {
                toggleEl.checked = false;
                applyProductImageZoomEnabled(false);
                return;
            }
            setStoredProductImageZoomEnabled(toggleEl.checked);
            applyProductImageZoomEnabled(toggleEl.checked);
        });
    });

    if (document.body?.dataset.imageZoomViewportBound !== "1") {
        document.body.dataset.imageZoomViewportBound = "1";
        bindProductImageZoomViewportResize(syncZoomToggleUi);
    }
}

console.log("[Vite] App.js loaded");

function onAppDomReady(): void {
    Logger.log("DOM Ready - Vite mode");

    initAvatarImageFallback();
    PostContentImageFallbackSetup.init();
    initNotifications();
    initRegistry.start();
    AdClickTrackingSetup.init();

    try {
        const { handler } = ModalSetup.init();
        setModalHandlerInstance(handler);
    } catch (error) {
        console.error("Failed to initialize modal system:", error);
    }

    if (!appInitsRegistered) {
        appInitsRegistered = true;

        initRegistry.register({
            id: "post-create",
            selector: `[data-role="${Roles.POST_CREATE_FORM}"]`,
            init: async (el) => {
                await PostCreateSetup.init(el);
            },
        });

        initRegistry.register({
            id: "wysiwyg",
            selector: 'textarea[data-wysiwyg="true"]',
            init: (el) => {
                if (el instanceof HTMLTextAreaElement) {
                    WysiwygSetup.init(el);
                }
            },
        });

        initRegistry.register({
            id: "post-show-modeless",
            selector: `[data-role="${Roles.POST_DETAIL}"][data-post-id]`,
            when: (el) => {
                if (appForm.isInsideElementModalOverlay(el)) return false;
                if (el instanceof HTMLElement && el.dataset.hiddenRole === Roles.POST_DETAIL_HIDDEN) return false;

                const isIndexPage = appForm.hasPostCard(document);
                if (isIndexPage) return false;

                if (el instanceof HTMLElement && el.offsetParent === null) return false;

                return true;
            },
            init: async () => {
                await PostShowSetup.init();
            },
        });

        initRegistry.register({
            id: "post-product-linker",
            selector: `[data-role="${Roles.POST_PRODUCT_LINKER}"], [data-role="${Roles.POST_PRODUCT_LINKER_VIEW}"]`,
            init: async (el) => {
                if (!(el instanceof HTMLElement)) return;
                if (el.dataset.postProductLinkerInit === "1") return;
                el.dataset.postProductLinkerInit = "1";
                const instance = new PostProductLinker(el);
                await instance.init();
            },
        });

        initRegistry.register({
            id: "posts-ads",
            selector: `[data-list-type="${Roles.POSTS_LIST}"]`,
            init: (el) => {
                if (el instanceof HTMLElement) {
                    PostsAdsSetup.init(el);
                }
            },
        });

        initRegistry.register({
            id: "jobs-dashboard",
            selector: `[data-role="${Roles.JOBS_DASHBOARD}"]`,
            init: async () => {
                await JobsDashboardSetup.init();
            },
        });

        initRegistry.register({
            id: "users-admin",
            selector: `[data-role="${Roles.USERS_ADMIN_ROOT}"]`,
            init: async () => {
                await UsersAdminSetup.init();
            },
        });

        initRegistry.register({
            id: "admin-dashboard",
            selector: `[data-role="${Roles.ADMIN_DASHBOARD_ROOT}"]`,
            init: async () => {
                await DashboardSetup.init();
            },
        });

        initRegistry.register({
            id: "board-configs-admin",
            selector: ".board-configs-admin",
            init: (el) => {
                BoardConfigAdminSetup.init(el);
            },
        });

        initRegistry.register({
            id: "point-rules-admin",
            selector: ".point-rules-admin",
            init: (el) => {
                PointRuleAdminSetup.init(el);
            },
        });

        initRegistry.register({
            id: "ads-admin",
            selector: ".ads-admin",
            init: (el) => {
                AdsAdminSetup.init(el);
            },
        });

        initRegistry.register({
            id: "post-product-thumb-image-error",
            selector: "body",
            init: () => {
                ProductThumbImageErrorHandlerSetup.init();
            },
        });

        initRegistry.register({
            id: "board-media-image-error",
            selector: "body",
            init: () => {
                BoardMediaImageErrorSetup.init();
            },
        });

        initRegistry.register({
            id: "daily-mission-progress",
            selector: ".mission-progress-fill[data-progress]",
            init: (el) => {
                const progress = el.getAttribute("data-progress");
                if (!progress) return;
                if (el instanceof HTMLElement) {
                    el.style.width = `${progress}%`;
                }
            },
        });

        initRegistry.register({
            id: "info-tooltip-system",
            selector: "body",
            init: () => {
                initInfoTooltipSystem();
            },
        });

        initRegistry.register({
            id: "sns-links-panel",
            selector: `[data-role="${Roles.SNS_LINKS_PANEL}"][data-post-id]`,
            init: (el) => {
                if (el instanceof HTMLElement) {
                    SNSLinksPanelSetup.init(el);
                }
            },
        });

        initRegistry.register({
            id: "user-avatar-upload",
            selector: `[data-role="${Roles.AVATAR_MODAL}"]`,
            init: () => {
                UserAvatarSetup.init();
            },
        });

        initRegistry.register({
            id: "user-profile-edit",
            selector: `[data-role="${Roles.PROFILE_EDIT_FORM}"]`,
            init: () => {
                UserEditSetup.init();
            },
        });

        initRegistry.register({
            id: "user-profile-tabs",
            selector: `[data-role="${Roles.PROFILE_DISPLAY_MODE}"]`,
            init: (el) => {
                if (el instanceof HTMLElement) {
                    UserProfileTabsSetup.init(el);
                }
            },
        });

        initRegistry.register({
            id: "user-profile-password",
            selector: ".profile-password-form[data-api-url]",
            init: (el) => {
                UserProfilePasswordSetup.init(el);
            },
        });

        initRegistry.register({
            id: "user-social-handle-check",
            selector: `[data-role="${Roles.SOCIAL_HANDLE_CHECK_STATUS}"]`,
            init: (el) => {
                UserSocialHandleCheckSetup.init(el);
            },
        });

        initRegistry.register({
            id: "user-follow-toggle",
            selector: `[data-role="${Roles.USER_FOLLOW}"][data-target-user-id]`,
            init: () => {
                UserFollowSetup.init();
            },
        });

        initRegistry.register({
            id: "master-data-edit",
            selector: `[data-role="${Roles.MASTER_DATA_EDITOR}"]`,
            init: () => {
                initMasterDataEdit();
            },
        });

        initRegistry.register({
            id: "merge-tool",
            selector: `[data-role="${Roles.MERGE_TOOL_MODAL}"]`,
            init: () => {
                initMergeTool();
            },
        });

        initRegistry.register({
            id: "auto-submit-toggle",
            selector: 'input[data-auto-submit="1"]',
            init: (el) => {
                AutoSubmitToggleSetup.init(el);
            },
        });

        initRegistry.register({
            id: "brands-filter-form",
            selector: ".brand-search-form",
            init: (el) => {
                if (el instanceof HTMLElement) {
                    BrandsFilterFormSetup.init(el);
                }
            },
        });

        initRegistry.register({
            id: "brands-sidebar",
            selector: `[data-role="${Roles.BRANDS_SIDEBAR}"]`,
            init: (el) => {
                initBrandsSidebar(el);
            },
        });

        initRegistry.register({
            id: "user-register",
            selector: `[data-role="${Roles.USER_REGISTER_FORM}"]`,
            init: (el) => {
                UserRegisterSetup.init(el);
            },
        });

        initRegistry.register({
            id: "rankings-monthly-tabs",
            selector: `[data-role="${Roles.RANKINGS_MONTHLY_TABS}"]`,
            init: (el) => {
                if (el instanceof HTMLElement) {
                    RankingsMonthlyTabsSetup.init(el);
                }
            },
        });

        initRegistry.register({
            id: "board-media-slider",
            selector: `[data-role="${Roles.BOARD_MEDIA_AREA}"]`,
            when: (el) => {
                // Content shown in the element modal body is visible even when its wrapper
                // still carries data-hidden-role (detached stash), so always allow init there.
                if (appForm.isInsideElementModalBody(el))
                    return true;
                return !appForm.isInsideHiddenPostDetail(el);
            },
            init: (el) => {
                if (el instanceof HTMLElement) {
                    BoardMediaSliderSetup.init(el);
                }
            },
        });

        initRegistry.register({
            id: "board-card-review-details",
            selector: `[data-role="${Roles.POST_CARD_REVIEW_DETAILS_TOGGLE}"]`,
            init: (el) => {
                if (el instanceof HTMLElement) {
                    BoardCardReviewDetailsHandler.init(BoardCardReviewDetailsForm.getClosestReviewDetails(el) || document);
                }
            },
        });

        initRegistry.register({
            id: "horizontal-slider",
            selector: `[data-role="${Roles.HORIZONTAL_SLIDER}"]`,
            init: (el) => {
                if (el instanceof HTMLElement) {
                    HorizontalSliderSetup.init(el);
                }
            },
        });

        initRegistry.register({
            id: "vanilla-slider",
            selector: '[data-role="vanilla-slider"]',
            init: (el) => {
                if (el instanceof HTMLElement) {
                    VanillaSliderSetup.init(el);
                }
            },
        });

        initRegistry.register({
            id: "home-ranking-product-tabs",
            selector: `[data-role="${Roles.HOME_RANKING_PRODUCT_TABS}"]`,
            init: (el) => {
                if (el instanceof HTMLElement) {
                    HomeRankingProductTabsSetup.init(el);
                    ProductPlaceholderAdsSetup.init(el);
                }
            },
        });

        initRegistry.register({
            id: "rankings-placeholder-product-ads",
            selector: `[data-role="${Roles.RANKINGS_MONTHLY_TABS}"]`,
            init: (el) => {
                if (el instanceof HTMLElement) {
                    ProductPlaceholderAdsSetup.init(el);
                }
            },
        });

        initRegistry.register({
            id: "tab-slider",
            selector: '[data-component="tab-slider"]',
            init: (el) => {
                if (el instanceof HTMLElement) {
                    TabSliderSetup.init(el);
                }
            },
        });

        initRegistry.register({
            id: "about-us-tabs",
            selector: ".about-us-page",
            init: (el) => {
                if (el instanceof HTMLElement) {
                    AboutUsTabsSetup.init(el);
                }
            },
        });

        initRegistry.register({
            id: "navbar-hamburger",
            selector: `[data-role="${Roles.NAVBAR_CONTENT}"]`,
            init: (el) => {
                if (el instanceof HTMLElement) {
                    NavbarHamburgerSetup.init(el);
                }
            },
        });

        initRegistry.register({
            id: "navbar-settings",
            selector: `[data-role="${Roles.NAVBAR_CONTENT}"]`,
            init: (el) => {
                if (el instanceof HTMLElement) {
                    NavbarSettingsSetup.init(el);
                }
            },
        });

        initRegistry.register({
            id: "products-infinite-scroll",
            selector: ".cards-grid.product",
            init: (el) => {
                if (el instanceof HTMLElement) {
                    ProductsInfiniteScrollSetup.init(el);
                }
            },
        });

        initRegistry.register({
            id: "product-sort-toggle",
            selector: `[data-role="${Roles.PRODUCT_SORT_TOGGLE}"]`,
            init: (el) => {
                if (el instanceof HTMLElement) {
                    ProductSortToggleSetup.init(el);
                }
            },
        });

        initRegistry.register({
            id: "export-posts-review-toggle",
            selector: `[data-role="${Roles.EXPORT_POSTS_CARD}"]`,
            init: (el) => {
                if (el instanceof HTMLElement) {
                    ExportPostsReviewToggleSetup.init(el);
                }
            },
        });

        initRegistry.register({
            id: "admin-export-async",
            selector: `[data-role="${Roles.EXPORT_FORM}"]`,
            init: (el) => {
                if (el instanceof HTMLElement) {
                    ExportAsyncSetup.init(el);
                }
            },
        });

        initRegistry.register({
            id: "import-file-clear",
            selector: `[data-role="${Roles.IMPORT_FILE_FIELD}"]`,
            init: (el) => {
                if (el instanceof HTMLElement) {
                    ImportFileClearSetup.init(el);
                }
            },
        });

        initRegistry.register({
            id: "brands-infinite-scroll",
            selector: ".brand-cards-grid",
            init: (el) => {
                if (el instanceof HTMLElement) {
                    BrandsInfiniteScrollSetup.init(el);
                }
            },
        });

        initRegistry.register({
            id: "posts-infinite-scroll",
            selector: `[data-list-type="${Roles.POSTS_LIST}"]`,
            init: (el) => {
                if (el instanceof HTMLElement) {
                    PostsInfiniteScrollSetup.init(el);
                }
            },
        });

        initRegistry.register({
            id: "filter-sidebar-toggle",
            selector: `[data-role="${Roles.FILTER_SIDEBAR_TOGGLE}"]`,
            init: (_el) => {
                initFilterSidebarToggle(document.body);
            },
        });

        // Runs on every page (the mobile button lives in the shared navbar, not
        // just pages with a sidebar) — hides it where it wouldn't do anything.
        initRegistry.register({
            id: "filter-sidebar-mobile-toggle-visibility",
            selector: `[data-role="${Roles.FILTER_SIDEBAR_MOBILE_TOGGLE}"]`,
            init: (_el) => {
                hideMobileFilterToggleIfInert(document.body);
            },
        });

        initRegistry.register({
            id: "filter-sidebar",
            selector: `[data-role="${Roles.FILTER_SIDEBAR}"]`,
            init: (el) => {
                initFilterSidebar(el);
            },
        });
    }

    initNavbarImageZoomToggle();
    syncInfiniteScrollViewportMode();
    if (document.body?.dataset.infiniteScrollViewportBound !== "1") {
        document.body.dataset.infiniteScrollViewportBound = "1";
        bindProductImageZoomViewportResize(syncInfiniteScrollViewportMode);
    }

    initProductCardSystem();
    initProductRecommendSystem();
    initPostRecommendSystem();
    initUserRecommendSystem();
    initBrandRecommendSystem();

    initRegistry.scan(document);

    initModalTitlebarDragAuto(document);
    startModalTitlebarDragObserver();

    new InstagramEmbedDebug().init();

    initSpinnerSystem();
    initFormValidator();
    initLikeSystem();
    SearchSuggestSetup.init(document);
    SNSPreviewManagerSetup.initAuto();
    MaximizeViewerSetup.init();
    FacebookMessengerLinkSetup.init();
    LoginHistorySetup.init();

    Logger.log("Environment:", {
        mode: import.meta.env.MODE,
        dev: import.meta.env.DEV,
        prod: import.meta.env.PROD,
    });

    CommentSetup.init();
    Logger.log("Comment System: Initialized");

    initializeDropdownMenus();
    dropdownHandler.initialize();
    Logger.log("Dropdown System: Initialized");

    // On pages that have list-card dropdowns but are NOT the dedicated post-show
    // page (e.g. product detail with review list cards), PostShowSetup.init() is
    // blocked by the isIndexPage guard. initPostShowActions() registers only the
    // event listeners needed for edit/delete/share without SNS processing or read tracking.
    if (appForm.hasPostCard(document)) {
        void initPostShowActions();
    }

    // DIRECT_MAXIMIZE boards: track reads when show-more is expanded by the user.
    document.addEventListener("show-more:toggle", (event) => {
        if (!(event instanceof CustomEvent) || !event.detail.expanded || event.detail.source !== "user") return;
        const target = event.target;
        if (!(target instanceof Element)) return;
        const cardBody = target.closest<HTMLElement>('[data-modal-usage="DIRECT_MAXIMIZE"]');
        directMaximizeReadTracker.trackFromCard(cardBody);
    });

    SearchMenuSetup.init(document);
    Logger.log("Search Menu System: Initialized");

    ImageZoomSetup.init(document);

    ShowMoreSetup.init(document);
    ShowMoreSetup.observe(document);

    setupScrollHide();
    setupFloatingChat();

    initMultiLangText({
        onAfterUpdate: () => {
            ShowMoreSetup.init(document);
        },
    });
}

bindAppDomReady(onAppDomReady);
